import { useState } from "react"
import { toast } from "sonner"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Badge } from "@/components/ui/badge"
import { ScheduleDialog } from "@/components/manager/ScheduleDialog"
import { useUpdateSchedule } from "@/lib/queries"
import { cn } from "@/lib/utils"
import type { ManagerAssignmentRow, QuizRoundRow } from "@/lib/types"

// Dates arrive as "YYYY-MM-DD" or an ISO timestamp. Date-only strings are
// built as local dates so the day does not shift with the viewer's timezone.
function parseDay(value: string): Date {
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  return dateOnly
    ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
    : new Date(value)
}

export function formatDay(value: string | null): string {
  if (!value) return "—"
  return parseDay(value).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  })
}

function formatMonth(value: string): string {
  return parseDay(value).toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
  })
}

function todayIso(): string {
  const now = new Date()
  const pad = (n: number) => String(n).padStart(2, "0")
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
}

// The latest submitted attempt has no managerial review yet.
export function awaitsReview(row: ManagerAssignmentRow): boolean {
  return row.completed_at !== null && row.manager_reviewed_at === null
}

type Tone = "green" | "amber" | "red" | "muted" | "blue"

const TONES: Record<Tone, string> = {
  green:
    "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400",
  amber:
    "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400",
  red: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
  blue: "bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-400",
  muted: "bg-secondary text-secondary-foreground",
}

export function Pill({ label, tone }: { label: string; tone: Tone }) {
  return (
    <Badge
      className={cn(
        "whitespace-nowrap border-transparent text-xs font-semibold",
        TONES[tone]
      )}
    >
      {label}
    </Badge>
  )
}

// One pill per person row. Order matters: a closed round awaiting review keeps
// the review marker, then open rows go locked, passed, overdue, then status.
function rowPill(row: ManagerAssignmentRow): { label: string; tone: Tone } {
  const passed = row.status === "passed"
  if (row.closed_at) {
    if (row.closed_reason !== "removed" && awaitsReview(row)) {
      return passed
        ? { label: "Passed · needs review", tone: "blue" }
        : { label: "Needs review", tone: "blue" }
    }
    if (row.closed_reason === "completed") return { label: "Completed", tone: "green" }
    if (row.closed_reason === "removed") return { label: "Removed", tone: "muted" }
    return { label: "Not completed", tone: "muted" }
  }
  if (row.locked) return { label: "Locked", tone: "red" }
  if (passed && awaitsReview(row)) return { label: "Passed · needs review", tone: "blue" }
  if (passed) return { label: "Passed", tone: "green" }
  const unfinished = row.status === "not_started" || row.status === "in_progress"
  if (unfinished && row.due_on && row.due_on < todayIso()) {
    return { label: "Overdue", tone: "red" }
  }
  if (row.status === "in_progress") return { label: "In progress", tone: "amber" }
  if (row.status === "not_started") return { label: "Not started", tone: "muted" }
  return { label: "Not passed", tone: "red" }
}

export function RowPill({ row }: { row: ManagerAssignmentRow }) {
  return <Pill {...rowPill(row)} />
}

export function ProgressBar({ done, total }: { done: number; total: number }) {
  return (
    <div className="flex items-center gap-2">
      <div className="h-2 w-24 overflow-hidden rounded-full bg-secondary">
        <div
          className="h-full rounded-full bg-emerald-500"
          style={{ width: `${total ? (done / total) * 100 : 0}%` }}
        />
      </div>
      <span className="text-sm text-muted-foreground">
        {total ? `${done} / ${total}` : "—"}
      </span>
    </div>
  )
}

function RepeatLine({ round }: { round: QuizRoundRow }) {
  const schedule = round.schedule
  const updateSchedule = useUpdateSchedule()
  const [editing, setEditing] = useState(false)
  const [stopping, setStopping] = useState(false)
  if (!schedule || !schedule.active || round.closed) return null

  const handleStop = async () => {
    try {
      await updateSchedule.mutateAsync({ id: schedule.id, active: false })
      toast.success("Stopped repeating")
    } catch {
      toast.error("Failed to stop repeating")
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
      <span>
        {schedule.interval_months === 12
          ? "Repeats every year"
          : "Repeats every 6 months"}
      </span>
      <span>Next round {formatDay(schedule.next_opens_on)}</span>
      <button
        type="button"
        className="text-foreground underline-offset-2 hover:underline"
        onClick={() => setEditing(true)}
      >
        Edit
      </button>
      <button
        type="button"
        className="text-destructive underline-offset-2 hover:underline"
        onClick={() => setStopping(true)}
      >
        Stop
      </button>

      <ScheduleDialog
        schedule={schedule}
        currentOpenedOn={round.opened_on}
        open={editing}
        onOpenChange={setEditing}
      />

      <AlertDialog open={stopping} onOpenChange={setStopping}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Stop repeating?</AlertDialogTitle>
            <AlertDialogDescription>
              The current round keeps running. No new rounds will open.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleStop}
              disabled={updateSchedule.isPending}
            >
              Stop
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

// One round (or the one-off assignments) of an assessment: a header with the
// round's dates, progress and repeat controls, then one row per person.
export function RoundGroup({
  title,
  round,
  rows,
  onSelectPerson,
}: {
  title: string
  round: QuizRoundRow | null
  rows: ManagerAssignmentRow[]
  onSelectPerson: (row: ManagerAssignmentRow) => void
}) {
  const finished = rows.filter((r) => r.completed_at !== null).length
  const sorted = [ ...rows ].sort((a, b) => a.staff_name.localeCompare(b.staff_name))

  return (
    <div className="border-t first:border-t-0">
      <div className="flex flex-wrap items-start justify-between gap-3 px-6 py-3">
        <div className="space-y-1">
          <p className="text-sm font-medium">
            {title}
            {round && (
              <span className="font-normal text-muted-foreground">
                {" "}· {formatMonth(round.opened_on)}
              </span>
            )}
          </p>
          {round && (
            <p className="text-xs text-muted-foreground">
              {round.due_on ? `Due ${formatDay(round.due_on)}` : "No due date"}
            </p>
          )}
          {round && <RepeatLine round={round} />}
        </div>
        <ProgressBar done={finished} total={rows.length} />
      </div>
      {rows.length === 0 && (
        <p className="border-t px-6 py-2 text-sm text-muted-foreground">
          No one in this round
        </p>
      )}
      <table className="w-full text-sm">
        <tbody>
          {sorted.map((row) => (
            <tr
              key={row.id}
              className="cursor-pointer border-t hover:bg-muted/50"
              onClick={() => onSelectPerson(row)}
            >
              <td className="px-6 py-2">
                <button
                  type="button"
                  className="text-left hover:underline"
                  onClick={(e) => {
                    e.stopPropagation()
                    onSelectPerson(row)
                  }}
                >
                  {row.staff_name}
                </button>
              </td>
              <td className="w-[160px] px-4 py-2 text-muted-foreground">
                {row.completed_at
                  ? `Done ${formatDay(row.completed_at)}`
                  : row.due_on
                    ? `Due ${formatDay(row.due_on)}`
                    : "No due date"}
              </td>
              <td className="w-[180px] px-4 py-2">
                <RowPill row={row} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
